import { message, Space } from 'antd';
import { UploadProps } from 'antd/es/upload/interface';
import { FileDownload } from 'components/UI/FileDownload';
import Spin from 'components/UI/Spin';
import { Uploader } from 'components/UI/Uploader';
import { filter } from 'es-toolkit/compat';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { routesApi } from 'routes/api';
import { useStore } from 'stores/RootStore';

const MAX_UPLOAD_SIZE_IN_MB = 75;

export const defaultBeforeUpload = (file: { type: string; size: number }): boolean => {
  const isLt75M = file.size / 1024 / 1024 < MAX_UPLOAD_SIZE_IN_MB;

  if (!isLt75M) {
    void message.error(`Image must be smaller than ${MAX_UPLOAD_SIZE_IN_MB}MB!`);
  }

  return isLt75M;
};

interface Props {
  beforeUpload?(this: void, file: { type: string; size: number }): boolean;
  fetchUrl: string;
  uploadUrl?: string;
  destroyUrl?: string;
  context?: string;
  hideUploader?: boolean;
}

const AttachedFilesUploader = observer<Props>((props) => {
  const { fetchUrl, uploadUrl, destroyUrl, context = 'additional', hideUploader = false } = props;
  const beforeUploadFn = props.beforeUpload ?? defaultBeforeUpload;
  const { attachedFilesStore } = useStore();

  useEffect(() => {
    void attachedFilesStore.fetch(fetchUrl, context);
  }, [fetchUrl, context, attachedFilesStore]);

  if (!attachedFilesStore.fetchStatus[context]?.isFetched) {
    return <Spin size="large" />;
  }

  const customRequest: UploadProps['customRequest'] = (params) => {
    if (uploadUrl == null) return;

    const formData = new FormData();
    formData.append('file', params.file);

    if (context) {
      formData.append('context', context);
    }

    void attachedFilesStore.upload(uploadUrl, formData, context);
  };

  const onDestroyClick = (id: number) => {
    if (destroyUrl) {
      void attachedFilesStore.delete(`${destroyUrl}?attached_file_id=${id}`, id);
    }
  };

  const isUploading = attachedFilesStore.uploadStatus[context]?.isProcessing ?? false;

  const fileList = context ? filter(attachedFilesStore.attachments, { context }) : attachedFilesStore.attachments;

  const sortedFiles = fileList.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  return (
    <Space orientation="vertical" size="small" className="w-100">
      {uploadUrl && !hideUploader && (
        <Uploader
          loading={isUploading}
          showUploadList={false}
          customRequest={customRequest}
          beforeUpload={beforeUploadFn}
          multiple
        />
      )}
      <Space orientation="vertical" size={4} className="w-100">
        {sortedFiles.map((file) => (
          <FileDownload
            key={file.id}
            id={file.id}
            url={routesApi.attachedFileDownloadPath(file.id)}
            name={file.name}
            createdAt={file.createdAt}
            open
            download
            onDelete={() => onDestroyClick(file.id)}
          />
        ))}
      </Space>
    </Space>
  );
});

export default AttachedFilesUploader;
